import React, { useEffect, useState } from "react";

import {
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
} from "@material-ui/core";

import { KeyboardDatePicker } from "@material-ui/pickers";

import SearchIcon from "@material-ui/icons/Search";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import { lighten, makeStyles } from "@material-ui/core/styles";

import AdvanceSearch from "./AdvanceSearch";

const useToolbarStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
    width: "100%",
  },

  toolbar: {
    minHeight: 64,
    padding: "8px 16px",
    position: "relative",

    /*
     * Important:
     * allows AdvancedSearch to appear above CardItem
     */
    overflow: "visible",

    [theme.breakpoints.down("sm")]: {
      padding: "10px",
    },
  },

  toolbarContent: {
    width: "100%",
    minHeight: 48,

    display: "flex",
    alignItems: "center",

    position: "relative",

    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      gap: 8,
    },
  },

  /*
   * ==========================================================
   * TITLE
   * ==========================================================
   */
  titleContainer: {
    display: "flex",
    alignItems: "center",

    flexShrink: 0,
    minWidth: 0,
  },

  title: {
    display: "block",

    fontSize: 20,
    fontWeight: 600,

    color: "#292929",

    whiteSpace: "nowrap",

    [theme.breakpoints.down("md")]: {
      fontSize: 18,
    },

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  /*
   * ==========================================================
   * CENTER SEARCH
   * ==========================================================
   */
  centerSearch: {
    position: "absolute",

    left: "50%",
    top: "50%",

    transform: "translate(-50%, -50%)",

    width: 400,

    /*
     * Higher than table
     */
    zIndex: 10000,

    [theme.breakpoints.down("md")]: {
      position: "relative",
      left: "auto",
      top: "auto",
      transform: "none",

      marginLeft: 20,

      width: 320,
    },

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      marginLeft: 0,
    },
  },

  searchTrigger: {
    position: "relative",

    height: 42,
    width: "100%",

    display: "flex",
    alignItems: "center",

    backgroundColor: "#fff",

    border: "1px solid #d8d8d8",
    borderRadius: theme.shape.borderRadius,

    cursor: "pointer",

    boxSizing: "border-box",

    "&:hover": {
      borderColor: "#999",
    },
  },

  searchIcon: {
    marginLeft: 12,
    marginRight: 8,

    color: "#777",

    fontSize: 21,
  },

  searchText: {
    flex: 1,

    color: "#999",

    fontSize: 15,
  },

  arrowButton: {
    padding: 5,
    marginRight: 4,

    color: "#666",
  },

  /*
   * ==========================================================
   * RIGHT FILTERS
   * ==========================================================
   *
   * These follow the same MUI pattern as your reference:
   * FormControlLabel + Checkbox + KeyboardDatePicker +
   * Button + clear IconButton.
   */
  rightFilters: {
    marginLeft: "auto",

    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",

    gap: 6,

    flexShrink: 0,

    [theme.breakpoints.down("md")]: {
      gap: 4,
    },

    [theme.breakpoints.down("sm")]: {
      width: "100%",

      flexWrap: "wrap",

      justifyContent: "flex-start",

      marginLeft: 0,
    },
  },

  checkbox: {
    margin: 0,

    whiteSpace: "nowrap",

    "& .MuiFormControlLabel-label": {
      fontSize: 14,
      fontWeight: 500,
    },

    "& .MuiCheckbox-root": {
      padding: 6,
    },
  },

  /*
   * Match reference KeyboardDatePicker style.
   */
  datePicker: {
    width: 125,

    "& .MuiInputBase-root": {
      fontSize: 14,
    },

    "& .MuiOutlinedInput-root": {
      height: 40,
    },

    "& .MuiOutlinedInput-input": {
      padding: "9px 8px",
    },

    "& .MuiInputLabel-outlined": {
      fontSize: 14,
    },

    "& .MuiIconButton-root": {
      padding: 7,
    },

    [theme.breakpoints.down("md")]: {
      width: 120,
    },
  },

  searchButton: {
    minWidth: "auto",

    padding: "6px 8px",

    fontSize: 13,

    color: "#292929",

    "& .MuiButton-endIcon": {
      marginLeft: 3,
    },
  },

  clearButton: {
    padding: 5,
    marginLeft: 0,
  },

  /*
   * ==========================================================
   * TOTALS
   * ==========================================================
   */
  secondSession: {
    display: "flex",

    flexDirection: "row-reverse",

    justifyContent: "space-between",
    alignItems: "center",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "row",
    },
  },

  clientTotalDiv: {
    background: "#fafafa",

    padding: "4px 8px",

    borderRadius: 4,

    display: "flex",

    flexDirection: "row",

    lineHeight: "0.78em",

    fontSize: 12,

    alignItems: "flex-end",

    margin: 10,

    "& span": {
      marginRight: "1em",

      backgroundColor: lighten(theme.palette.info.light, 0.7),

      padding: "10px 6px",

      borderRadius: 4,

      whiteSpace: "nowrap",
    },

    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",

      lineHeight: "1.4em",

      fontSize: 10,

      marginBottom: 6,
    },
  },
}));

const emptyQuickFilters = {
  businessType: "",
  hasVat: false,
  fromDate: null,
  toDate: null,
  isSearch: false,
};

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();

  const {
    title,

    count,
    totalAmount,
    totalVAT,
    totalInvoice,

    /*
     * Advanced Search
     */
    isAdvancedSearch,
    handleAdvancedSearch,

    clearAdvancedSearchHandler,
    searchAdvancedHandler,

    isClients,
    isInterpreters,

    handleInputChange,
    handleInterpreterInputChange,

    isSearchObj,

    /*
     * Right-side filters
     */
    searchHandler,
    clearSearchHandler,
  } = props;

  /*
   * ==========================================================
   * QUICK FILTER STATE
   *
   * IMPORTANT:
   * Date values remain Date objects here.
   * Do NOT convert them to ISO here.
   * ==========================================================
   */
  const [values, setValues] = useState({
    ...emptyQuickFilters,
  });

  /*
   * ==========================================================
   * KEEP TOOLBAR IN SYNC WITH PARENT STATE
   * ==========================================================
   */
  useEffect(() => {
    if (!isSearchObj) {
      return;
    }

    setValues((prev) => ({
      ...prev,

      businessType: isSearchObj.private ? "Private" : "",

      hasVat: !!isSearchObj.hasVat,

      fromDate: isSearchObj.fromDate ? new Date(isSearchObj.fromDate) : null,

      toDate: isSearchObj.toDate ? new Date(isSearchObj.toDate) : null,

      isSearch: !!isSearchObj.isSearch,
    }));
  }, [
    isSearchObj?.private,
    isSearchObj?.hasVat,
    isSearchObj?.fromDate,
    isSearchObj?.toDate,
    isSearchObj?.isSearch,
  ]);

  /*
   * ==========================================================
   * PRIVATE / VAT
   * ==========================================================
   */
  const handleChange = (event) => {
    const { name, checked } = event.target;

    setValues((prev) => ({
      ...prev,

      [name]: name === "businessType" ? (checked ? "Private" : "") : checked,
    }));
  };

  /*
   * ==========================================================
   * FROM DATE
   * ==========================================================
   */
  const handleFromDateChange = (date) => {
    setValues((prev) => ({
      ...prev,

      fromDate: date || null,
    }));
  };

  /*
   * ==========================================================
   * TO DATE
   * ==========================================================
   */
  const handleToDateChange = (date) => {
    setValues((prev) => ({
      ...prev,

      toDate: date || null,
    }));
  };

  /*
   * ==========================================================
   * RIGHT-SIDE SEARCH
   *
   * The parent merges these filters with the existing
   * Advanced Search filters.
   * ==========================================================
   */
  const handleQuickSearch = () => {
    searchHandler({
      businessType: values.businessType,
      hasVat: values.hasVat,
      fromDate: values.fromDate,
      toDate: values.toDate,
    });

    setValues((prev) => ({
      ...prev,

      isSearch: true,
    }));
  };

  /*
   * ==========================================================
   * CLEAR
   *
   * Appears only after Search has been clicked.
   * ==========================================================
   */
  const handleClearSearch = () => {
    setValues({
      ...emptyQuickFilters,
    });

    if (clearSearchHandler) {
      clearSearchHandler();
    }
  };

  return (
    <div className={classes.grow}>
      <AppBar
        position="static"
        color="default"
        elevation={1}
        style={{
          overflow: "visible",
        }}
      >
        <Toolbar className={classes.toolbar}>
          <div className={classes.toolbarContent}>
            {/* =================================================
                LEFT - TITLE
            ================================================= */}
            <div className={classes.titleContainer}>
              <Typography className={classes.title} variant="h6" noWrap>
                {title}
              </Typography>
            </div>

            {/* =================================================
                CENTER - ONLY ONE SEARCH TRIGGER
            ================================================= */}
            <div className={classes.centerSearch}>
              <div
                className={classes.searchTrigger}
                onClick={handleAdvancedSearch}
                role="button"
                tabIndex={0}
              >
                <SearchIcon className={classes.searchIcon} />

                <span className={classes.searchText}>Search</span>

                <IconButton
                  size="small"
                  className={classes.arrowButton}
                  onClick={(event) => {
                    event.stopPropagation();

                    handleAdvancedSearch();
                  }}
                >
                  {isAdvancedSearch ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
              </div>

              {/* =================================================
                  ADVANCED SEARCH PANEL

                  IMPORTANT:
                  There is only ONE Search trigger.
                  AdvanceSearch itself does NOT render a trigger.
              ================================================= */}
              {isAdvancedSearch && (
                <AdvanceSearch
                  isOpen={isAdvancedSearch}
                  handleToggle={handleAdvancedSearch}
                  clearAdvancedSearchHandler={clearAdvancedSearchHandler}
                  searchAdvancedHandler={searchAdvancedHandler}
                  isClients={isClients}
                  isInterpreters={isInterpreters}
                  handleInputChange={handleInputChange}
                  handleInterpreterInputChange={handleInterpreterInputChange}
                  isSearchObj={isSearchObj}
                />
              )}
            </div>

            {/* =================================================
                RIGHT - QUICK FILTERS
            ================================================= */}
            <div className={classes.rightFilters}>
              {/* PRIVATE */}
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={values.businessType === "Private"}
                    onChange={handleChange}
                    name="businessType"
                    color="primary"
                    size="small"
                  />
                }
                label="Private"
              />

              {/* VAT */}
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={values.hasVat}
                    onChange={handleChange}
                    name="hasVat"
                    color="primary"
                    size="small"
                  />
                }
                label="VAT"
              />

              {/* FROM */}
              <KeyboardDatePicker
                autoOk
                size="small"
                format="dd/MM/yyyy"
                variant="inline"
                inputVariant="outlined"
                label="From"
                value={values.fromDate}
                onChange={handleFromDateChange}
                KeyboardButtonProps={{
                  "aria-label": "change from date",
                }}
                className={classes.datePicker}
              />

              {/* TO */}
              <KeyboardDatePicker
                autoOk
                size="small"
                format="dd/MM/yyyy"
                variant="inline"
                inputVariant="outlined"
                label="To"
                value={values.toDate}
                onChange={handleToDateChange}
                KeyboardButtonProps={{
                  "aria-label": "change to date",
                }}
                className={classes.datePicker}
              />

              {/* SEARCH */}
              <Button
                size="small"
                endIcon={<SearchIcon />}
                onClick={handleQuickSearch}
                className={classes.searchButton}
              >
                Search
              </Button>

              {/* CLEAR */}
              {values.isSearch && (
                <Tooltip title="Clear search">
                  <IconButton
                    color="primary"
                    size="small"
                    aria-label="clear search inputs"
                    onClick={handleClearSearch}
                    className={classes.clearButton}
                  >
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        </Toolbar>
      </AppBar>

      {/* =====================================================
          TOTALS
      ====================================================== */}
      <div className={classes.secondSession}>
        <div className={classes.clientTotalDiv}>
          <span>
            <strong>Total Amount:</strong>{" "}
            {totalAmount ? totalAmount.toFixed(2) : "0.00"}
          </span>

          <span>
            <strong>VAT:</strong> {totalVAT ? totalVAT.toFixed(2) : "0.00"}
          </span>

          <span>
            <strong>Invoice Total:</strong>{" "}
            {totalInvoice ? totalInvoice.toFixed(2) : "0.00"}
          </span>

          <span>
            <strong>Count:</strong> {count || 0}
          </span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(EnhancedTableToolbar);
